/**
 * AdminUsersPage — Admin-only. Create/edit/disable/reset users.
 * Listing via GET /api/admin/users; CRUD via POST/PUT/DELETE.
 */
import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Checkbox } from "@/components/ui/checkbox";
import { Loader2, Plus, KeyRound, UserPlus, Power, PowerOff, Trash2, Copy, Check, AlertTriangle, Eye, EyeOff, RefreshCw } from "lucide-react";
import PageHelp from "@/components/PageHelp";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ROLES = ["admin", "manager", "reviewer", "user"];

export default function AdminUsersPage() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);
  const [credentials, setCredentials] = useState(null);

  const load = () => {
    setLoading(true);
    axios.get(`${API}/admin/users`, { withCredentials: true })
      .then((r) => setUsers(r.data))
      .catch((e) => toast.error(e?.response?.data?.detail || "Laden fehlgeschlagen"))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const updateField = async (userId, patch) => {
    try {
      await axios.put(`${API}/admin/users/${userId}`, patch, { withCredentials: true });
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Update fehlgeschlagen");
    }
  };

  const resetPw = (u) => setResetTarget(u);

  const [resetTarget, setResetTarget] = useState(null);

  const remove = async (u) => {
    if (!window.confirm(`Benutzer ${u.email} unwiderruflich loeschen?`)) return;
    try {
      await axios.delete(`${API}/admin/users/${u.user_id}`, { withCredentials: true });
      toast.success("Geloescht");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Loeschen fehlgeschlagen");
    }
  };

  return (
    <div data-testid="admin-users-page" className="space-y-6 max-w-5xl">
      <PageHelp title="Was kann ich auf dieser Seite tun?" storageKey="admin-users">
        <p>
          Hier verwaltest du wer sich im Studio einloggen darf. Es gibt <b>keine Selbst-Registrierung</b> — jeder Benutzer muss von dir hier angelegt werden.
        </p>
        <h4>Rollen</h4>
        <ul>
          <li><b>admin</b> — voller Zugriff inkl. dieser Seite, LLM-Keys, SMTP, alle Firmen</li>
          <li><b>manager</b> — kann Firmen verwalten, Posts erstellen + freigeben, aber keine Admin-Settings</li>
          <li><b>reviewer</b> — kann nur Posts freigeben/ablehnen (Approval-Schritt)</li>
          <li><b>user</b> — Standard-Redakteur: Posts erstellen, Wochenplaner, Medien</li>
        </ul>
        <h4>Neuen Benutzer anlegen</h4>
        <ol>
          <li>Oben rechts <b>„Neuer Benutzer"</b> klicken.</li>
          <li>E-Mail, Name, Rolle eintragen.</li>
          <li><b>Passwort</b>:
            <ul>
              <li><i>Feld leer lassen</i> → System erzeugt ein sicheres Passwort und schickt es per E-Mail (SMTP muss konfiguriert sein, siehe E-Mail-Konfiguration). Benutzer muss es beim ersten Login aendern.</li>
              <li><i>Passwort eingeben</i> → Du gibst es selbst an den Benutzer weiter. Kein Aenderungszwang.</li>
            </ul>
          </li>
        </ol>
        <h4>Buttons in der Liste</h4>
        <ul>
          <li>🔑 <b>Schluessel</b> — Passwort zuruecksetzen (gleiche Logik wie beim Anlegen)</li>
          <li>🚫 <b>Deaktivieren</b> — Konto sperren, ohne es zu loeschen (alle Sessions werden beendet)</li>
          <li>🗑️ <b>Loeschen</b> — endgueltig entfernen. Der letzte aktive Admin kann nicht geloescht werden.</li>
        </ul>
      </PageHelp>
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-4xl font-bold font-heading tracking-tight">Benutzerverwaltung</h1>
          <p className="text-[var(--studio-text-muted)] mt-1">
            Nur Administratoren koennen Konten anlegen, deaktivieren oder loeschen
          </p>
        </div>
        <Button
          onClick={() => setCreateOpen(true)}
          className="bg-[#2563EB] hover:bg-[#1D4ED8] text-white"
          data-testid="open-create-user-btn"
        >
          <UserPlus className="w-4 h-4 mr-1" /> Neuer Benutzer
        </Button>
      </div>

      <Card className="border-[var(--studio-border)] shadow-none">
        <CardHeader><CardTitle className="font-heading text-base">Alle Benutzer ({users.length})</CardTitle></CardHeader>
        <CardContent className="space-y-2" data-testid="users-list">
          {loading ? (
            <Loader2 className="w-4 h-4 animate-spin" />
          ) : users.length === 0 ? (
            <p className="text-xs text-[var(--studio-text-muted)]">Keine Benutzer</p>
          ) : users.map((u) => (
            <div
              key={u.user_id}
              className={`flex items-center gap-3 p-3 border border-[var(--studio-border)] rounded-lg ${u.active === false ? "opacity-50 bg-gray-50" : ""}`}
              data-testid={`user-${u.user_id}`}
            >
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium truncate">{u.name || u.email}</p>
                <p className="text-xs text-[var(--studio-text-muted)] truncate">{u.email}</p>
                <div className="flex flex-wrap items-center gap-1.5 mt-1 text-[10px]">
                  {u.must_change_password && (
                    <Badge className="bg-amber-100 text-amber-800 border-0">Passwort-Reset erforderlich</Badge>
                  )}
                  {u.active === false && (
                    <Badge className="bg-gray-300 text-gray-800 border-0">Deaktiviert</Badge>
                  )}
                  {u.last_login_at && (
                    <span className="text-[var(--studio-text-muted)]">
                      Letzter Login: {new Date(u.last_login_at).toLocaleString("de-CH", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" })}
                    </span>
                  )}
                </div>
              </div>
              <Select value={u.role} onValueChange={(v) => updateField(u.user_id, { role: v })}>
                <SelectTrigger className="w-32 h-8" data-testid={`role-${u.user_id}`}>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {ROLES.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}
                </SelectContent>
              </Select>
              <Button
                size="sm"
                variant="ghost"
                onClick={() => resetPw(u)}
                title="Passwort zuruecksetzen"
                data-testid={`reset-${u.user_id}`}
              >
                <KeyRound className="w-4 h-4" />
              </Button>
              <Button
                size="sm"
                variant="ghost"
                onClick={() => updateField(u.user_id, { active: !(u.active !== false) })}
                title={u.active === false ? "Aktivieren" : "Deaktivieren"}
                data-testid={`toggle-${u.user_id}`}
              >
                {u.active === false ? <Power className="w-4 h-4 text-green-600" /> : <PowerOff className="w-4 h-4" />}
              </Button>
              <Button
                size="sm"
                variant="ghost"
                onClick={() => remove(u)}
                className="text-red-500"
                data-testid={`delete-${u.user_id}`}
              >
                <Trash2 className="w-4 h-4" />
              </Button>
            </div>
          ))}
        </CardContent>
      </Card>

      <CreateUserDialog
        open={createOpen}
        onOpenChange={setCreateOpen}
        onCreated={(c) => { setCreateOpen(false); setCredentials(c); load(); }}
      />

      <ResetPasswordDialog
        target={resetTarget}
        onClose={() => setResetTarget(null)}
        onReset={(c) => { setResetTarget(null); setCredentials(c); load(); }}
      />

      <CredentialsDialog
        creds={credentials}
        onClose={() => setCredentials(null)}
      />
    </div>
  );
}

const RESET_OPTIONS = [
  { key: "companies", label: "Firmen", desc: "Alle Firmen + Brand-Voice-Profile + Snapshots" },
  { key: "media", label: "Medien", desc: "Alle Bilder, Videos und KI-Video-Job-Historie" },
  { key: "content", label: "Inhalte", desc: "Posts, Reels, Stories, Kommentare, Review-Links, Kampagnen, A/B-Tests, Wochenplaene, Aktivitaetslog" },
  { key: "rss_feeds", label: "RSS-Feeds", desc: "Alle konfigurierten Feeds (Smart-Filter inklusive)" },
  { key: "templates", label: "Vorlagen", desc: "Prompt-Templates + Marketplace inkl. Upvotes/Reports" },
  { key: "feedback", label: "Feedback", desc: "Alle NPS-Eintraege und User-Feedback" },
  { key: "non_admin_users", label: "Nicht-Admin-Benutzer", desc: "Loescht alle User die nicht 'admin' sind (Admins bleiben erhalten)" },
];

function DangerZone({ onResetDone }) {
  const [selected, setSelected] = useState({});
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const selectedKeys = Object.keys(selected).filter((k) => selected[k]);
  const hasSelection = selectedKeys.length > 0;

  const toggle = (k) => setSelected((s) => ({ ...s, [k]: !s[k] }));

  const submit = async () => {
    setSubmitting(true);
    try {
      const res = await axios.post(
        `${API}/admin/system/reset`,
        { categories: selectedKeys },
        { withCredentials: true },
      );
      const total = Object.values(res.data?.wiped || {}).reduce((a, b) => a + (b || 0), 0);
      toast.success(`Zuruecksetzen abgeschlossen. ${total} Eintraege geloescht.`);
      setSelected({});
      setConfirmOpen(false);
      onResetDone?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Zuruecksetzen fehlgeschlagen");
    }
    setSubmitting(false);
  };

  return (
    <Card
      className="border-red-300 bg-red-50/40 shadow-none"
      data-testid="danger-zone-card"
    >
      <CardHeader>
        <CardTitle className="font-heading text-base flex items-center gap-2 text-red-700">
          <AlertTriangle className="w-4 h-4" /> Gefahrenzone — System zuruecksetzen
        </CardTitle>
        <p className="text-xs text-red-700/80 mt-1">
          Loescht ausgewaehlte Datenkategorien unwiderruflich. Admin-Konten und SMTP-Konfiguration bleiben immer erhalten.
        </p>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="grid sm:grid-cols-2 gap-2" data-testid="reset-options">
          {RESET_OPTIONS.map((opt) => (
            <label
              key={opt.key}
              htmlFor={`reset-${opt.key}`}
              className="flex items-start gap-2 p-3 border border-red-200 rounded-lg bg-white cursor-pointer hover:bg-red-50/60"
              data-testid={`reset-opt-${opt.key}`}
            >
              <Checkbox
                id={`reset-${opt.key}`}
                checked={!!selected[opt.key]}
                onCheckedChange={() => toggle(opt.key)}
                data-testid={`reset-cb-${opt.key}`}
              />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium">{opt.label}</p>
                <p className="text-[11px] text-[var(--studio-text-muted)]">{opt.desc}</p>
              </div>
            </label>
          ))}
        </div>
        <div className="flex justify-end pt-1">
          <Button
            variant="destructive"
            disabled={!hasSelection}
            onClick={() => setConfirmOpen(true)}
            data-testid="reset-system-btn"
          >
            <Trash2 className="w-4 h-4 mr-1" />
            {hasSelection ? `${selectedKeys.length} Kategorie(n) zuruecksetzen` : "Kategorien auswaehlen"}
          </Button>
        </div>
      </CardContent>

      <Dialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <DialogContent className="sm:max-w-md" data-testid="reset-confirm-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <AlertTriangle className="w-5 h-5" /> Wirklich zuruecksetzen?
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3 text-sm">
            <p>Folgende Kategorien werden <b>unwiderruflich</b> geloescht:</p>
            <ul className="list-disc list-inside space-y-1 text-red-700">
              {selectedKeys.map((k) => (
                <li key={k}>{RESET_OPTIONS.find((o) => o.key === k)?.label}</li>
              ))}
            </ul>
            <p className="text-xs text-[var(--studio-text-muted)]">
              Admin-Konten + SMTP-Konfiguration bleiben erhalten.
            </p>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setConfirmOpen(false)}
              disabled={submitting}
              data-testid="reset-cancel-btn"
            >
              Abbrechen
            </Button>
            <Button
              variant="destructive"
              onClick={submit}
              disabled={submitting}
              data-testid="reset-confirm-btn"
            >
              {submitting ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Trash2 className="w-4 h-4 mr-1" />}
              Ja, jetzt loeschen
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}

function CreateUserDialog({ open, onOpenChange, onCreated }) {
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [role, setRole] = useState("user");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [sendEmail, setSendEmail] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState(null);

  const reset = () => {
    setEmail(""); setName(""); setRole("user");
    setPassword(""); setShowPassword(false); setSendEmail(false); setErr(null);
  };

  const genRandom = () => {
    // 14 chars, URL-safe-ish, mixed case + digits
    const buf = new Uint8Array(14);
    window.crypto.getRandomValues(buf);
    const chars = "abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789";
    let out = "";
    for (const b of buf) out += chars[b % chars.length];
    setPassword(out);
    setShowPassword(true);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (password && password.length < 8) {
      setErr("Passwort muss mind. 8 Zeichen haben");
      return;
    }
    setSubmitting(true);
    setErr(null);
    try {
      const payload = { email, name, role };
      if (password) {
        payload.password = password;
        payload.send_welcome_email = sendEmail;
      }
      const res = await axios.post(
        `${API}/admin/users`,
        payload,
        { withCredentials: true }
      );
      onCreated({ ...res.data, action: "create" });
      reset();
    } catch (e) {
      setErr(e?.response?.data?.detail || "Fehler");
    }
    setSubmitting(false);
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { onOpenChange(v); if (!v) reset(); }}>
      <DialogContent className="sm:max-w-md" data-testid="create-user-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading flex items-center gap-2">
            <Plus className="w-5 h-5 text-[#2563EB]" /> Neuer Benutzer
          </DialogTitle>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          <div>
            <label className="text-xs text-[var(--studio-text-muted)]">E-Mail *</label>
            <Input
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="user@firma.ch"
              className="mt-1"
              data-testid="new-user-email"
            />
          </div>
          <div>
            <label className="text-xs text-[var(--studio-text-muted)]">Name</label>
            <Input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Max Mustermann"
              className="mt-1"
              data-testid="new-user-name"
            />
          </div>
          <div>
            <label className="text-xs text-[var(--studio-text-muted)]">Rolle</label>
            <Select value={role} onValueChange={setRole}>
              <SelectTrigger className="mt-1" data-testid="new-user-role"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="user">User (Standard)</SelectItem>
                <SelectItem value="reviewer">Reviewer (Freigabe)</SelectItem>
                <SelectItem value="manager">Manager</SelectItem>
                <SelectItem value="admin">Administrator</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="pt-2 border-t border-[var(--studio-border)]">
            <div className="flex items-center justify-between">
              <label className="text-xs text-[var(--studio-text-muted)]">
                Passwort <span className="text-[10px]">(optional — leer lassen fuer Auto-Generator)</span>
              </label>
              <button
                type="button"
                onClick={genRandom}
                className="text-[11px] text-[#2563EB] hover:underline flex items-center gap-1"
                data-testid="generate-password-btn"
              >
                <RefreshCw className="w-3 h-3" /> Generieren
              </button>
            </div>
            <div className="relative mt-1">
              <Input
                type={showPassword ? "text" : "password"}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Leer = Auto-Passwort per E-Mail"
                className="pr-9"
                autoComplete="new-password"
                data-testid="new-user-password"
              />
              <button
                type="button"
                onClick={() => setShowPassword((s) => !s)}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-[var(--studio-text-muted)] hover:text-black"
                data-testid="toggle-password-visibility"
              >
                {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
            {password && (
              <label className="flex items-center gap-2 mt-2 text-xs text-[var(--studio-text-muted)] cursor-pointer">
                <Checkbox
                  checked={sendEmail}
                  onCheckedChange={(v) => setSendEmail(!!v)}
                  data-testid="send-welcome-email-checkbox"
                />
                Welcome-E-Mail mit Zugangsdaten zusaetzlich versenden
              </label>
            )}
            <p className="text-[10px] text-[var(--studio-text-muted)] mt-2">
              {password
                ? "Manuell gesetzt: Benutzer muss das Passwort beim ersten Login NICHT aendern."
                : "Ohne Eingabe wird ein sicheres Passwort generiert und per E-Mail versendet. Benutzer muss es beim ersten Login aendern."}
            </p>
          </div>

          {err && <p className="text-xs text-red-500">{err}</p>}
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Abbrechen</Button>
            <Button
              type="submit"
              disabled={submitting}
              className="bg-[#2563EB] hover:bg-[#1D4ED8] text-white"
              data-testid="submit-create-user"
            >
              {submitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Plus className="w-4 h-4 mr-2" />}
              Anlegen
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function ResetPasswordDialog({ target, onClose, onReset }) {
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [sendEmail, setSendEmail] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState(null);

  useEffect(() => {
    if (target) {
      setPassword(""); setShowPassword(false); setSendEmail(false); setErr(null);
    }
  }, [target]);

  if (!target) return null;

  const genRandom = () => {
    const buf = new Uint8Array(14);
    window.crypto.getRandomValues(buf);
    const chars = "abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789";
    let out = "";
    for (const b of buf) out += chars[b % chars.length];
    setPassword(out);
    setShowPassword(true);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (password && password.length < 8) {
      setErr("Passwort muss mind. 8 Zeichen haben");
      return;
    }
    setSubmitting(true);
    setErr(null);
    try {
      const payload = {};
      if (password) {
        payload.password = password;
        payload.send_welcome_email = sendEmail;
      }
      const res = await axios.post(
        `${API}/admin/users/${target.user_id}/reset-password`,
        payload,
        { withCredentials: true },
      );
      onReset({
        email: target.email,
        initial_password: res.data.initial_password,
        manual_password: res.data.manual_password,
        welcome_email_sent: res.data.welcome_email_sent,
        welcome_email_skipped: res.data.welcome_email_skipped,
        action: "reset",
      });
    } catch (e) {
      setErr(e?.response?.data?.detail || "Reset fehlgeschlagen");
    }
    setSubmitting(false);
  };

  return (
    <Dialog open={true} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="sm:max-w-md" data-testid="reset-password-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading flex items-center gap-2">
            <KeyRound className="w-5 h-5 text-[#2563EB]" /> Passwort zuruecksetzen
          </DialogTitle>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          <div className="text-xs bg-amber-50 border border-amber-200 rounded p-2">
            <b>{target.email}</b> — alle aktiven Sessions werden beendet.
          </div>
          <div>
            <div className="flex items-center justify-between">
              <label className="text-xs text-[var(--studio-text-muted)]">
                Neues Passwort <span className="text-[10px]">(optional — leer lassen fuer Auto-Generator)</span>
              </label>
              <button
                type="button"
                onClick={genRandom}
                className="text-[11px] text-[#2563EB] hover:underline flex items-center gap-1"
                data-testid="reset-generate-password-btn"
              >
                <RefreshCw className="w-3 h-3" /> Generieren
              </button>
            </div>
            <div className="relative mt-1">
              <Input
                type={showPassword ? "text" : "password"}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Leer = Auto-Passwort per E-Mail"
                className="pr-9"
                autoComplete="new-password"
                data-testid="reset-password-input"
              />
              <button
                type="button"
                onClick={() => setShowPassword((s) => !s)}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-[var(--studio-text-muted)] hover:text-black"
                data-testid="reset-toggle-password-visibility"
              >
                {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
            {password && (
              <label className="flex items-center gap-2 mt-2 text-xs text-[var(--studio-text-muted)] cursor-pointer">
                <Checkbox
                  checked={sendEmail}
                  onCheckedChange={(v) => setSendEmail(!!v)}
                  data-testid="reset-send-welcome-email-checkbox"
                />
                Welcome-E-Mail mit Zugangsdaten zusaetzlich versenden
              </label>
            )}
            <p className="text-[10px] text-[var(--studio-text-muted)] mt-2">
              {password
                ? "Manuell gesetzt: Benutzer muss das Passwort beim ersten Login NICHT aendern."
                : "Ohne Eingabe wird ein sicheres Passwort generiert und per E-Mail versendet. Benutzer muss es beim ersten Login aendern."}
            </p>
          </div>

          {err && <p className="text-xs text-red-500">{err}</p>}
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>Abbrechen</Button>
            <Button
              type="submit"
              disabled={submitting}
              className="bg-[#2563EB] hover:bg-[#1D4ED8] text-white"
              data-testid="submit-reset-password"
            >
              {submitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <KeyRound className="w-4 h-4 mr-2" />}
              Passwort zuruecksetzen
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function CredentialsDialog({ creds, onClose }) {
  const [copied, setCopied] = useState(false);
  if (!creds) return null;

  const message = `E-Mail: ${creds.email}\nInitial-Passwort: ${creds.initial_password}\n\nBitte beim ersten Login dein eigenes Passwort vergeben.`;

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(message);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch { /* noop */ }
  };

  return (
    <Dialog open={true} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md" data-testid="credentials-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading">
            {creds.action === "create" ? "Benutzer erstellt ✅" : "Passwort zurueckgesetzt ✅"}
          </DialogTitle>
        </DialogHeader>
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
          <p className="text-xs text-amber-900 font-semibold mb-2">⚠️ WICHTIG: Diese Daten werden NUR EINMAL angezeigt</p>
          <div className="bg-white rounded p-3 font-mono text-sm space-y-1">
            <p><b>E-Mail:</b> {creds.email}</p>
            <p><b>Passwort:</b> <span className="bg-yellow-100 px-1" data-testid="credentials-password">{creds.initial_password}</span></p>
          </div>
          <p className="text-xs text-amber-900 mt-2">
            Sende die Zugangsdaten sicher an den Benutzer. Beim ersten Login wird ein Passwortwechsel erzwungen.
          </p>
          {creds.welcome_email_sent === true && (
            <p className="text-xs text-green-700 mt-2 flex items-center gap-1" data-testid="welcome-email-sent">
              ✓ Welcome-E-Mail wurde automatisch an {creds.email} gesendet
            </p>
          )}
          {creds.welcome_email_skipped === true && (
            <p className="text-xs text-[var(--studio-text-muted)] mt-2 flex items-center gap-1" data-testid="welcome-email-skipped">
              ℹ Welcome-E-Mail wurde auf deinen Wunsch NICHT versendet — bitte Zugangsdaten selbst weitergeben.
            </p>
          )}
          {creds.welcome_email_sent === false && creds.welcome_email_skipped !== true && (
            <p className="text-xs text-red-700 mt-2" data-testid="welcome-email-not-sent">
              ⚠ Welcome-E-Mail konnte nicht gesendet werden — bitte Zugangsdaten manuell weitergeben (siehe E-Mail-Konfiguration)
            </p>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={copy} data-testid="copy-credentials-btn">
            {copied ? <><Check className="w-4 h-4 mr-1" /> Kopiert</> : <><Copy className="w-4 h-4 mr-1" /> Zugangsdaten kopieren</>}
          </Button>
          <Button onClick={onClose} className="bg-[#2563EB] text-white">OK</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
